import React, { useId } from "react";
import { twMerge } from "tailwind-merge";

import Card from "@components/layout/Card";
import LineScale from "@components/layout/LineScale";
import { formatVolume } from "@utils/formatVolume";

type WaterClassUsage = {
  id: string;
  name: string;
  /** Litres; shown beside the class. */
  usage: number;
  /**
   * How full the bar is, as a fraction of its width; not clamped. Above 1 the
   * bar runs past its end; below 0, or when not a number, it fills its track.
   */
  share: number;
};

type ZoneUsageCardProps = {
  name: string;
  subscriberCount: number;
  subscribersLabel: string;
  waterClasses: WaterClassUsage[];
  /** Shown once in place of every class's usage and bar, which belong to an accounting period; absent while one is current. */
  periodNotice?: string;
  /**
   * Marks the card up as a region named by the zone, whose name is its only
   * heading, and names each class's item by the class. Off, the subscriber
   * count is a heading too and nothing is named, as Polestar's dashboard has it.
   */
  landmark?: boolean;
  className?: string;
};

/**
 * A zone's subscribers and its water classes' usage. Polestar's scheme dashboard
 * and Zenith's both show it; each page gives its own figures and wording.
 */
const ZoneUsageCard: React.FunctionComponent<ZoneUsageCardProps> = ({
  name,
  subscriberCount,
  subscribersLabel,
  waterClasses,
  periodNotice,
  landmark = false,
  className,
}) => {
  const nameId = useId();
  const classId = useId();
  const Count = landmark ? "p" : "h1";
  const label = (id: string) => (landmark ? id : undefined);

  return (
    <Card
      role={landmark ? "region" : undefined}
      aria-labelledby={label(nameId)}
      className={twMerge("shadow-none w-full", className)}
      header={
        <h4 id={label(nameId)} className="text-gray-900 -mb-4">
          {name}
        </h4>
      }
    >
      <div className="flex flex-col gap-2">
        <Count className="flex gap-1 text-3xl items-baseline">
          {subscriberCount} <span className="text-sm">{subscribersLabel}</span>
        </Count>
        {periodNotice ? (
          <p className="text-gray-500 text-sm">{periodNotice}</p>
        ) : null}
        <ul className="flex flex-col gap-2 mt-2">
          {waterClasses.map(waterClass => (
            <li
              className="flex flex-col"
              key={waterClass.id}
              aria-labelledby={label(`${classId}-${waterClass.id}`)}
            >
              <div className="flex justify-between text-sm">
                <p id={label(`${classId}-${waterClass.id}`)}>
                  {waterClass.name}
                </p>
                {periodNotice ? null : (
                  <p className="text-green-500">
                    {formatVolume(waterClass.usage)}
                  </p>
                )}
              </div>
              {periodNotice ? null : (
                <LineScale
                  scale={waterClass.share}
                  trackClassName="bg-usage-track"
                  fillClassName="bg-usage-fill"
                />
              )}
            </li>
          ))}
        </ul>
      </div>
    </Card>
  );
};

export default ZoneUsageCard;
